/**
 * Track geometry — shared by client (rendering, prediction) and server (authority).
 *
 * A track is a *band* around a closed centerline: a point is on the track iff its
 * distance to the centerline is ≤ halfWidth − carRadius. The outside is a region,
 * not a thin wall, so tunneling through a wall is geometrically impossible — no
 * continuous collision detection needed.
 *
 * The centerline is a closed centripetal Catmull-Rom spline through control points,
 * resampled at a uniform step. Each sample carries position, unit tangent, half
 * width and curvilinear abscissa `s` (used for lap progress, checkpoints, resets).
 */

import { CAR_CAPSULE_HALF_LENGTH } from './collision';
import type { Vec2 } from './math';
import { clamp, dist, lerp, normalize, perp, projectOnSegment } from './math';
import type { CarState, PhysicsConfig, SurfaceId } from './physics';

export interface TrackControlPoint {
  x: number;
  y: number;
  /** Full corridor width at this point, in px. */
  width: number;
  /** Surface of the segment STARTING at this point (default 'asphalt'). */
  surface?: SurfaceId;
}

export interface TrackSample {
  x: number;
  y: number;
  /** Unit tangent (driving direction). */
  tx: number;
  ty: number;
  halfWidth: number;
  /** Curvilinear abscissa from the start line, in px. */
  s: number;
  /** Surface under this portion of track — drives the active physics config. */
  surface: SurfaceId;
}

export interface Track {
  samples: TrackSample[];
  /** Total centerline length in px. */
  length: number;
  /** Uniform spacing between consecutive samples, in px. */
  step: number;
  /** Start/finish line segment (left edge → right edge). */
  startLine: { a: Vec2; b: Vec2 };
  /** Spawn pose on the start line. */
  spawn: { x: number; y: number; angle: number };
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
}

export interface ClosestPointResult {
  point: Vec2;
  tangent: Vec2;
  halfWidth: number;
  /** Curvilinear abscissa of the projection, in px. */
  s: number;
  /** Distance from the query point to the centerline. */
  dist: number;
  /** Unit vector from the projection toward the query point (outward radial). */
  radial: Vec2;
  /** Surface at the projection — the physics config to drive with. */
  surface: SurfaceId;
}

/** sqrt-based length — exactly rounded per ECMA-262, unlike Math.hypot (see math.ts). */
function segLen(dx: number, dy: number): number {
  return Math.sqrt(dx * dx + dy * dy);
}

/** Sampling resolution along the centerline. */
const SAMPLE_STEP = 6;
/** Coarse search stride for closest-point queries (see clearance analysis in tests). */
const COARSE_STRIDE = 4;
const RAW_SAMPLES_PER_SEGMENT = 64;

/**
 * Centripetal Catmull-Rom (α = 0.5) evaluation between p1 and p2, u ∈ [0,1].
 * Centripetal parameterization avoids loops and cusps with irregular control points.
 */
function catmullRom(p0: Vec2, p1: Vec2, p2: Vec2, p3: Vec2, u: number): Vec2 {
  const alpha = 0.5;
  const t0 = 0;
  const t1 = t0 + Math.max(Math.pow(dist(p0, p1), alpha), 1e-4);
  const t2 = t1 + Math.max(Math.pow(dist(p1, p2), alpha), 1e-4);
  const t3 = t2 + Math.max(Math.pow(dist(p2, p3), alpha), 1e-4);
  const t = t1 + (t2 - t1) * u;

  const a1 = lerpPoint(p0, p1, (t - t0) / (t1 - t0));
  const a2 = lerpPoint(p1, p2, (t - t1) / (t2 - t1));
  const a3 = lerpPoint(p2, p3, (t - t2) / (t3 - t2));
  const b1 = lerpPoint(a1, a2, (t - t0) / (t2 - t0));
  const b2 = lerpPoint(a2, a3, (t - t1) / (t3 - t1));
  return lerpPoint(b1, b2, (t - t1) / (t2 - t1));
}

function lerpPoint(a: Vec2, b: Vec2, t: number): Vec2 {
  return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) };
}

function smoothstep01(t: number): number {
  const c = clamp(t, 0, 1);
  return c * c * (3 - 2 * c);
}

export interface BuildTrackOptions {
  /**
   * Shifts the start/finish line forward along the centerline by this many px
   * (relative to the first control point) — lets us place it on a straight.
   */
  startOffset?: number;
}

/**
 * Dense point along the closed raw centerline polyline, before uniform
 * resampling. Produced by the Catmull-Rom builder (generated circuits) and by
 * the cubic-Bézier builder (hand-drawn circuits, see design.ts).
 */
export interface RawCenterPoint {
  x: number;
  y: number;
  halfWidth: number;
  surface: SurfaceId;
}

/**
 * Builds a track from control points: closed centripetal Catmull-Rom centerline,
 * uniformly resampled, with widths blended smoothly between control points.
 */
export function buildTrack(points: TrackControlPoint[], opts: BuildTrackOptions = {}): Track {
  if (points.length < 4) {
    throw new Error(`buildTrack: need at least 4 control points, got ${points.length}`);
  }
  const n = points.length;

  // 1. Dense raw polyline along the closed spline, carrying interpolated widths.
  // Each raw point inherits the surface of the control segment it belongs to.
  const raw: RawCenterPoint[] = [];
  for (let i = 0; i < n; i++) {
    const p0 = points[(i - 1 + n) % n] as TrackControlPoint;
    const p1 = points[i] as TrackControlPoint;
    const p2 = points[(i + 1) % n] as TrackControlPoint;
    const p3 = points[(i + 2) % n] as TrackControlPoint;
    const surface = p1.surface ?? 'asphalt';
    for (let k = 0; k < RAW_SAMPLES_PER_SEGMENT; k++) {
      const u = k / RAW_SAMPLES_PER_SEGMENT;
      const pos = catmullRom(p0, p1, p2, p3, u);
      const halfWidth = lerp(p1.width, p2.width, smoothstep01(u)) / 2;
      raw.push({ x: pos.x, y: pos.y, halfWidth, surface });
    }
  }

  return assembleTrackFromRaw(raw, opts.startOffset ?? 0);
}

/**
 * Shared tail of every track builder: uniform resampling of a dense closed raw
 * polyline, start-line rotation, tangents, spawn and bounds. Kept bit-exact
 * with the historical buildTrack pipeline — stored circuits must rebuild
 * identically forever (records depend on it).
 */
export function assembleTrackFromRaw(raw: RawCenterPoint[], startOffset: number): Track {
  // 2. Cumulative arc length of the raw polyline (closed: last→first included).
  const rawCount = raw.length;
  const cumulative: number[] = new Array<number>(rawCount + 1);
  cumulative[0] = 0;
  for (let i = 0; i < rawCount; i++) {
    const a = raw[i] as { x: number; y: number };
    const b = raw[(i + 1) % rawCount] as { x: number; y: number };
    cumulative[i + 1] = (cumulative[i] as number) + segLen(b.x - a.x, b.y - a.y);
  }
  const totalLength = cumulative[rawCount] as number;
  if (totalLength < SAMPLE_STEP * 8) {
    throw new Error('buildTrack: degenerate track (centerline too short)');
  }

  // 3. Uniform resampling.
  const count = Math.max(Math.round(totalLength / SAMPLE_STEP), 32);
  const step = totalLength / count;
  const samples: TrackSample[] = new Array<TrackSample>(count);
  let seg = 0;
  for (let i = 0; i < count; i++) {
    const target = i * step;
    while (seg < rawCount - 1 && (cumulative[seg + 1] as number) < target) seg++;
    const segStart = cumulative[seg] as number;
    const segEnd = cumulative[seg + 1] as number;
    const t = segEnd > segStart ? (target - segStart) / (segEnd - segStart) : 0;
    const a = raw[seg] as RawCenterPoint;
    const b = raw[(seg + 1) % rawCount] as RawCenterPoint;
    samples[i] = {
      x: lerp(a.x, b.x, t),
      y: lerp(a.y, b.y, t),
      tx: 0,
      ty: 0,
      halfWidth: lerp(a.halfWidth, b.halfWidth, t),
      s: target,
      surface: a.surface,
    };
  }

  // 4. Optional rotation so that sample 0 (= start line) sits where we want it.
  // Spacing is uniform, so rotating the array and re-stamping s = i·step is exact.
  const shift = ((Math.round(startOffset / step) % count) + count) % count;
  if (shift > 0) {
    const rotated = samples.slice(shift).concat(samples.slice(0, shift));
    for (let i = 0; i < count; i++) {
      const smp = rotated[i] as TrackSample;
      smp.s = i * step;
      samples[i] = smp;
    }
  }

  // 5. Tangents by central difference (wrapping).
  for (let i = 0; i < count; i++) {
    const prev = samples[(i - 1 + count) % count] as TrackSample;
    const next = samples[(i + 1) % count] as TrackSample;
    const t = normalize({ x: next.x - prev.x, y: next.y - prev.y });
    (samples[i] as TrackSample).tx = t.x;
    (samples[i] as TrackSample).ty = t.y;
  }

  // 6. Start line, spawn, bounds.
  const s0 = samples[0] as TrackSample;
  const normal = perp({ x: s0.tx, y: s0.ty });
  const startLine = {
    a: { x: s0.x - normal.x * s0.halfWidth, y: s0.y - normal.y * s0.halfWidth },
    b: { x: s0.x + normal.x * s0.halfWidth, y: s0.y + normal.y * s0.halfWidth },
  };
  const spawn = { x: s0.x, y: s0.y, angle: Math.atan2(s0.ty, s0.tx) };

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const smp of samples) {
    minX = Math.min(minX, smp.x - smp.halfWidth);
    minY = Math.min(minY, smp.y - smp.halfWidth);
    maxX = Math.max(maxX, smp.x + smp.halfWidth);
    maxY = Math.max(maxY, smp.y + smp.halfWidth);
  }

  return {
    samples,
    length: totalLength,
    step,
    startLine,
    spawn,
    bounds: { minX, minY, maxX, maxY },
  };
}

/** Coarse global scan (stride 4 ≈ 24 px) + neighbours: best sample index. */
function coarseClosestIdx(track: Track, p: Vec2): number {
  const samples = track.samples;
  const count = samples.length;

  let bestIdx = 0;
  let bestD2 = Infinity;
  for (let i = 0; i < count; i += COARSE_STRIDE) {
    const smp = samples[i] as TrackSample;
    const dx = p.x - smp.x;
    const dy = p.y - smp.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      bestIdx = i;
    }
  }
  for (let off = -COARSE_STRIDE + 1; off < COARSE_STRIDE; off++) {
    if (off === 0) continue;
    const i = (bestIdx + off + count) % count;
    const smp = samples[i] as TrackSample;
    const dx = p.x - smp.x;
    const dy = p.y - smp.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Local scan around a hint index (per-sample, ±span): best sample index for a
 * point KNOWN to project near the hint (the capsule ends, ±4 px from a car
 * centre already resolved by the global search). The clearance rule keeps any
 * other track section far beyond a span of 8 samples (≈ 48 px of arc), so the
 * local winner is the global one.
 */
function localClosestIdx(track: Track, p: Vec2, hint: number, span: number): number {
  const samples = track.samples;
  const count = samples.length;
  let bestIdx = ((hint % count) + count) % count;
  let bestD2 = Infinity;
  for (let off = -span; off <= span; off++) {
    const i = (hint + off + count) % count;
    const smp = samples[i] as TrackSample;
    const dx = p.x - smp.x;
    const dy = p.y - smp.y;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Closest point on the centerline. Coarse scan (stride 4 ≈ 24 px) then local
 * refinement and projection on the two adjacent segments. Exactness is guaranteed
 * by the track clearance rule (distinct track sections are far apart relative to
 * the stride) — enforced by unit tests on every track we ship or generate.
 */
export function closestPoint(track: Track, p: Vec2): ClosestPointResult {
  return refineClosestAt(track, p, coarseClosestIdx(track, p));
}

/** Projection + interpolation around a known best sample (closestPoint's tail). */
function refineClosestAt(track: Track, p: Vec2, bestIdx: number): ClosestPointResult {
  const samples = track.samples;
  const count = samples.length;

  // Project on the segments on both sides of the best sample.
  const prevIdx = (bestIdx - 1 + count) % count;
  const nextIdx = (bestIdx + 1) % count;
  const sPrev = samples[prevIdx] as TrackSample;
  const sBest = samples[bestIdx] as TrackSample;
  const sNext = samples[nextIdx] as TrackSample;

  const projA = projectOnSegment(p, sPrev, sBest);
  const projB = projectOnSegment(p, sBest, sNext);
  const dA = segLen(p.x - projA.point.x, p.y - projA.point.y);
  const dB = segLen(p.x - projB.point.x, p.y - projB.point.y);

  let from: TrackSample;
  let to: TrackSample;
  let t: number;
  let d: number;
  let point: Vec2;
  if (dA <= dB) {
    from = sPrev;
    to = sBest;
    t = projA.t;
    d = dA;
    point = projA.point;
  } else {
    from = sBest;
    to = sNext;
    t = projB.t;
    d = dB;
    point = projB.point;
  }

  const tangent = normalize({
    x: lerp(from.tx, to.tx, t),
    y: lerp(from.ty, to.ty, t),
  });
  const halfWidth = lerp(from.halfWidth, to.halfWidth, t);
  // `s` interpolation: `to.s` may wrap to 0 at the seam; use the step distance instead.
  let s = from.s + track.step * t;
  if (s >= track.length) s -= track.length;

  const radial = d > 1e-6 ? { x: (p.x - point.x) / d, y: (p.y - point.y) / d } : perp(tangent);
  const surface = t <= 0.5 ? from.surface : to.surface;

  return { point, tangent, halfWidth, s, dist: d, radial, surface };
}

export interface WallCollisionResult {
  state: CarState;
  hit: boolean;
  /** Speed component that went into the wall (px/s) — impact strength for FX/sound. */
  impact: number;
  /** Closest-point query, reused by callers (lap progress, HUD) to avoid recomputing. */
  query: ClosestPointResult;
}

/** Samples scanned around the centre's best index for the capsule-end queries. */
const END_QUERY_SPAN = 2 * COARSE_STRIDE;

/**
 * Impact ratio (vn/|v|) at which the wall rebound reaches its full
 * `wallRestitution`; below, it fades linearly to 0. A shallow graze must NOT
 * bounce: a rebound there makes the car SKIP along the wall (contact one tick
 * out of N), dodging most of the per-contact scrub — the exact loophole that
 * made wall-riding profitable. Frank hits (ratio ≥ this) keep the full
 * bumper-car rebound.
 */
const WALL_FULL_REBOUND_RATIO = 0.35;

/**
 * Keeps a car inside the track band, sliding along walls.
 *
 * Since the bumper-car rework (2026-07-21) the car meets the wall with its
 * CAPSULE (the drawn rubber ring), not its centre point: both capsule ends
 * (centre ± CAR_CAPSULE_HALF_LENGTH along the heading, radius carRadius) must
 * stay inside the band. Side contact is unchanged (the ring's flank is at
 * carRadius, as before), but the nose and tail now reach where the sprite
 * reaches — a head-on hit bounces ON the rubber instead of sinking 4 px in.
 *
 * The deepest-penetrating end is pushed back onto the limit; the outward
 * normal velocity is reflected with `wallRestitution` and the tangential
 * velocity is reduced proportionally to the impact angle (grazing costs almost
 * nothing, head-on costs the most). A second pass settles the other end when a
 * pinch presses both (positional only — one bounce per contact). The returned
 * `query` is the CENTRE query: lap timing and the active surface keep their
 * historical semantics. Deterministic — pure clamped arithmetic.
 */
export function resolveTrackCollision(
  state: CarState,
  track: Track,
  cfg: PhysicsConfig,
): WallCollisionResult {
  const centerIdx = coarseClosestIdx(track, { x: state.x, y: state.y });
  const query = refineClosestAt(track, { x: state.x, y: state.y }, centerIdx);

  const cosA = Math.cos(state.angle);
  const sinA = Math.sin(state.angle);
  let x = state.x;
  let y = state.y;
  let vx = state.vx;
  let vy = state.vy;
  let hit = false;
  let impact = 0;

  for (let pass = 0; pass < 2; pass++) {
    // Deepest-penetrating capsule end this pass (front checked first — fixed
    // order, strict inequality ⇒ deterministic tie-break).
    let pen = 0;
    let radial: Vec2 | null = null;
    for (const dir of END_DIRS) {
      const ex = x + cosA * CAR_CAPSULE_HALF_LENGTH * dir;
      const ey = y + sinA * CAR_CAPSULE_HALF_LENGTH * dir;
      const q = refineClosestAt(
        track,
        { x: ex, y: ey },
        localClosestIdx(track, { x: ex, y: ey }, centerIdx, END_QUERY_SPAN),
      );
      const p = q.dist - (q.halfWidth - cfg.carRadius);
      if (p > pen) {
        pen = p;
        radial = q.radial;
      }
    }
    if (!radial) break;
    hit = true;
    x -= radial.x * pen;
    y -= radial.y * pen;

    const vn = vx * radial.x + vy * radial.y;
    if (pass === 0) {
      // Split into outward normal and tangential parts. The tangential loss
      // has TWO terms: the impact-angle one (head-on costs, historical), plus
      // the flat per-contact scrub (wallScrub) applied on EVERY contact tick —
      // leaning on a wall compounds it 60×/s, far past what the engine can
      // repay, so riding the outside wall of a corner is always slower than
      // driving it. The rebound fades out on shallow contacts (see
      // WALL_FULL_REBOUND_RATIO): a graze hugs, a frank hit bounces.
      const outN = Math.max(vn, 0);
      const tvx = vx - radial.x * outN;
      const tvy = vy - radial.y * outN;
      const speed = segLen(vx, vy);
      const impactRatio = speed > 1e-6 ? outN / speed : 1;
      const rebound = cfg.wallRestitution * Math.min(1, impactRatio / WALL_FULL_REBOUND_RATIO);
      const keep = Math.max(0, 1 - cfg.wallScrub - cfg.wallFriction * impactRatio);
      vx = tvx * keep - radial.x * outN * rebound;
      vy = tvy * keep - radial.y * outN * rebound;
      impact = outN;
    } else if (vn > 0) {
      // Second end in a pinch: just stop the outward drift, no second bounce.
      vx -= radial.x * vn;
      vy -= radial.y * vn;
    }
  }

  if (!hit) {
    return { state, hit: false, impact: 0, query };
  }
  return {
    state: { x, y, angle: state.angle, vx, vy },
    hit: true,
    impact,
    query,
  };
}

/** Capsule end directions along the heading: nose first, then tail. */
const END_DIRS = [1, -1] as const;

/**
 * Signed forward arc distance from `s0` to `s1` on a closed track of length `L`,
 * in (-L/2, L/2]. Positive = progress in the driving direction.
 */
export function forwardArcDelta(s0: number, s1: number, length: number): number {
  let d = (s1 - s0) % length;
  if (d > length / 2) d -= length;
  if (d <= -length / 2) d += length;
  return d;
}

/** Does the forward arc (from, from+ds] cross the gate at `gate` (mod length)? */
export function crossedGateForward(
  from: number,
  ds: number,
  gate: number,
  length: number,
): boolean {
  let delta = (gate - from) % length;
  if (delta < 0) delta += length;
  return delta > 0 && delta <= ds;
}

/**
 * Two-column starting grid behind the start line: pose of `slot` (0-based).
 * Single source of truth used by the server to grid the cars AND by the track
 * editor to preview exactly where they will stand.
 */
export function gridSlotPose(track: Track, slot: number): CarState {
  const row = Math.floor(slot / 2);
  const col = slot % 2;
  const back = 40 + row * 42;
  let s = track.length - back;
  if (s < 0) s += track.length;
  const idx = Math.round(s / track.step) % track.samples.length;
  const smp = track.samples[idx] as TrackSample;
  const nx = -smp.ty;
  const ny = smp.tx;
  const lateral = (col === 0 ? -1 : 1) * smp.halfWidth * 0.35;
  return {
    x: smp.x + nx * lateral,
    y: smp.y + ny * lateral,
    angle: Math.atan2(smp.ty, smp.tx),
    vx: 0,
    vy: 0,
  };
}

/** Does the backward arc [from+ds, from) cross the gate (ds < 0)? */
export function crossedGateBackward(
  from: number,
  ds: number,
  gate: number,
  length: number,
): boolean {
  let delta = (from - gate) % length;
  if (delta < 0) delta += length;
  return delta > 0 && delta <= -ds;
}
